import { http } from '@/api/client/httpClient';
import type { PagedResult } from '@/api/types/abp';
import type {
  GetPermissionListResultDto,
  IdentityRoleDto,
  IdentityUserCreateDto,
  IdentityUserDto,
  IdentityUserUpdateDto,
  IdentityDatatableInput,
  SetActiveDto,
  UpdatePermissionsDto,
  UserDatatableDto,
} from '@/api/types/identity';
import { PERMISSION_PROVIDER_USER } from '@/api/types/identity';

// Routes from ABP conventional-controller mapping of the Finkit custom
// Finkit.IdentityUserManagement.IdentityUserAppService (the service the legacy
// Identity/Users page calls as finkit.identityUserManagement.identityUser.*).
// Verified against the running app's access log:
//   GetUsersWithRolesForDatatableAsync -> GET  /api/app/identity-user/users-with-roles-for-datatable
//   GetAsync(id)                       -> GET  /api/app/identity-user/{id}
//   CreateAsync(input)                 -> POST /api/app/identity-user
//   UpdateAsync(id, input)             -> PUT  /api/app/identity-user/{id}
//   DeleteAsync(id)                    -> DELETE /api/app/identity-user/{id}
//   SetActiveAsync(id, input)          -> POST /api/app/identity-user/{id}/active
//   GetRolesAsync(id)                  -> GET  /api/app/identity-user/{id}/roles
//   GetAssignableRolesAsync()          -> GET  /api/app/identity-user/assignable-roles
//   SendEmailVerificationAsync(id)     -> POST /api/app/identity-user/{id}/email-verification
const BASE = '/api/app/identity-user';

// Volo.Abp.PermissionManagement conventional controller.
const PERMISSIONS = '/api/permission-management/permissions';

interface ListResult<T> {
  items: T[];
}

export const identityUsersApi = {
  async getListForDataTable(
    input: IdentityDatatableInput,
  ): Promise<PagedResult<UserDatatableDto>> {
    const { data } = await http.get<PagedResult<UserDatatableDto>>(
      `${BASE}/users-with-roles-for-datatable`,
      { params: input },
    );
    return data;
  },

  async get(id: string): Promise<IdentityUserDto> {
    const { data } = await http.get<IdentityUserDto>(`${BASE}/${id}`);
    return data;
  },

  async create(input: IdentityUserCreateDto): Promise<IdentityUserDto> {
    const { data } = await http.post<IdentityUserDto>(BASE, input);
    return data;
  },

  async update(id: string, input: IdentityUserUpdateDto): Promise<IdentityUserDto> {
    const { data } = await http.put<IdentityUserDto>(`${BASE}/${id}`, input);
    return data;
  },

  async remove(id: string): Promise<void> {
    await http.delete(`${BASE}/${id}`);
  },

  async setActive(id: string, input: SetActiveDto): Promise<void> {
    await http.post(`${BASE}/${id}/set-active`, input);
  },

  async sendEmailVerification(id: string): Promise<void> {
    await http.post(`${BASE}/${id}/send-email-verification`);
  },

  async getRoles(id: string): Promise<IdentityRoleDto[]> {
    const { data } = await http.get<ListResult<IdentityRoleDto>>(`${BASE}/${id}/roles`);
    return data.items;
  },

  async getAssignableRoles(): Promise<IdentityRoleDto[]> {
    const { data } = await http.get<ListResult<IdentityRoleDto>>(`${BASE}/assignable-roles`);
    return data.items;
  },

  async getPermissions(userId: string): Promise<GetPermissionListResultDto> {
    const { data } = await http.get<GetPermissionListResultDto>(PERMISSIONS, {
      params: { providerName: PERMISSION_PROVIDER_USER, providerKey: userId },
    });
    return data;
  },

  async updatePermissions(userId: string, input: UpdatePermissionsDto): Promise<void> {
    await http.put(PERMISSIONS, input, {
      params: { providerName: PERMISSION_PROVIDER_USER, providerKey: userId },
    });
  },
};
